import { useEffect, type ReactNode } from "react";

/**
 * The screens that show Privacy · Terms · Support (#669; the `footer` requirement's `screens`
 * in `contract/screens.json`): sign-in, where signing up agrees to the Terms; voice setup
 * and re-record (`/rerecord` is the `enroll` screen), beside the voice-data promise; and
 * Account, below the red Delete account caption, so a signed-in person can always reach
 * them (#1250).
 */
export const FOOTER_SCREENS: ReadonlySet<string> = new Set(["signin", "enroll", "account"]);

/**
 * One screen, and the two things every screen change has always done.
 *
 * `document.body.dataset.screen` is not bookkeeping: `app.css` keys off it to hide the app
 * header on the sign-in front door (`body[data-screen="signin"] > header`). That selector
 * used to need a `front-door` class as well, because one markup section served both the
 * door and the signed-in Account page and something had to say which; they are separate
 * routes now, so the route is the whole condition. The other names are the ones the six
 * `<section id="screen-*">` blocks used, because the stylesheet still spells them that way.
 *
 * The element is a `<section>` and it is a direct child of `<main>`, which is why the
 * router mounts on `<main>` itself. `main > section` carries the slide-in; a wrapper
 * between them drops it silently.
 *
 * Scrolling to the top belongs here rather than in a router-wide listener: it is what a
 * screen change means, and a route that one day wants to restore a position should be
 * able to say so by not using this.
 *
 * `document.body.dataset.footer` shows or hides the static shell's Privacy · Terms footer
 * (`body[data-footer="on"] > .site-foot` in `app.css`), for the same reason: the footer is
 * in `index.html` outside the React root, so the screen says whether it carries it.
 */
export function Screen({
  name,
  children,
  still = false,
}: {
  name: string;
  children: ReactNode;
  /**
   * No slide-in. The first-run intro (#1095) takes over from the boot overlay with its mark
   * exactly where boot's is, and a transform on this section would move it, and everything
   * fixed inside it, for the length of the slide.
   */
  still?: boolean;
}) {
  useEffect(() => {
    document.body.dataset.screen = name;
    document.body.dataset.footer = FOOTER_SCREENS.has(name) ? "on" : "off";
    window.scrollTo({ top: 0 });
  }, [name]);

  // Two things carried over from the six static sections this replaced. The id, because
  // `app.css` has rules written against it (`#screen-diagnostics h4`) and they matched
  // nothing while this was a bare `<section>` -- one screen is mounted at a time, so two
  // routes stamping the same name (`/signin` and `/account` both render the account
  // screen) cannot collide. And the slide-in, once per screen shown; somebody who has
  // asked not to be moved gets the screen without it.
  return (
    <section id={`screen-${name}`} className={still ? undefined : "animate-screen-in motion-reduce:animate-none"}>
      {children}
    </section>
  );
}
